/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:03]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    Form,
    Modal,
    requiredRule,
    SettingTable,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import useAuth from 'hooks/useAuth';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormGroup, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import 'scss/Main.scss';
import { useSettingsWorkplaceState } from './Settings';

type SettingsChangeModalProps = {
    toggle: Dispatch<SetStateAction<boolean>>;
    isOpen: boolean;
};

export default function SettingsChangeModal({ isOpen = false, toggle }: SettingsChangeModalProps) {
    const { t } = useTranslation();
    const { cultures } = useGeneralSettingsContext();
    const { hasRole } = useAuth();
    const createRole = 'websettingsCreate';
    const updateRole = 'websettingsUpdate';

    const initialItem: SettingTable = {
        code: '',
        translations: cultures.map((c) => ({ cultureCode: c.code, value: '', dsc: '' })),
    };

    const { currentItem, changeItemMutation, addItemMutation } = useSettingsWorkplaceState();

    const saveChanges = async (data: SettingTable) => {
        currentItem
            ? changeItemMutation.mutate({ ...data, codeExists: data.code })
            : addItemMutation.mutate(data);

        closeModal();
    };

    const closeModal = () => {
        toggle(false);
    };

    const formHasRole = (addRole: string, editRole: string): boolean =>
        currentItem ? hasRole(editRole) : hasRole(addRole);

    const disabledInput = useMemo(() => !formHasRole(createRole, updateRole), []);

    const defaultValues = useMemo(() => ({ ...initialItem, ...currentItem }), [currentItem]);

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={closeModal}
            scrollable
            className="webSettings__modal"
            data-testid="settings-modal"
        >
            <Form<SettingTable>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValues}
                className="form-in-modal"
            >
                <ModalHeader toggle={closeModal}>
                    {currentItem ? `${currentItem.code}` : t('BackOfficeNew')}
                </ModalHeader>

                <ModalBody className="webSettings__modal-body custom-scrollbar">
                    <FormGroup>
                        <ValidatedInput
                            name="code"
                            label={t('BackOfficeCode')}
                            disabled={!!currentItem || !formHasRole(createRole, updateRole)}
                            rules={{ required: requiredRule }}
                        />
                    </FormGroup>
                    {cultures.map((x, idx) => {
                        return (
                            <FormGroup key={`info-${x.code}-group`}>
                                <hr />
                                <ValidatedInput
                                    key={`info-${x.code}-lang-field`}
                                    name={`translations[${idx}].value`}
                                    label={x.name}
                                    disabled={disabledInput}
                                />
                                <ValidatedInput
                                    key={`info-${x.code}-dsc-field`}
                                    name={`translations[${idx}].dsc`}
                                    label={t('BackOfficeDescription') + ' ' + x.code}
                                    disabled={disabledInput}
                                />
                            </FormGroup>
                        );
                    })}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={closeModal}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" disabled={!formHasRole(createRole, updateRole)}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
